import React, { useEffect, useMemo, useState } from "react";
import "./PolicyModal.css";
import { authFetch } from "../../utils/authFetch";
import CustomFieldsSection from "../custom-fields/CustomFieldsSection";

const PLAN_ID_COVERAGE_TYPES = ["MAPD", "PDP"];

const getPlanYearFromDate = (dateValue) => {
  if (!dateValue) return "";
  const year = new Date(`${dateValue}T00:00:00`).getFullYear();
  return Number.isNaN(year) ? "" : String(year);
};

const shouldShowTerminationDate = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  return normalized === "terminated" || normalized === "replaced";
};

const getCarrierId = (carrier) =>
  carrier?.id ?? carrier?.carrier_id ?? carrier?.carrierId ?? "";

const PolicyModal = ({
  policy = {},
  products = [],
  onClose,
  onSave,
  onDelete
}) => {
  const [formData, setFormData] = useState({
    id: null,
    status: "",
    carrierId: "",
    planName: "",
    planId: "",
    coverageTypeId: "",
    coverageTypePlanTypeId: "",
    applicationDate: "",
    effectiveDate: "",
    renewalDate: "",
    terminationDate: "",
    electionPeriod: "",
    hraCompleted: "",
    policyNumber: "",
    enrollmentCode: "",
    premium: "",
    benefitOption: "",
    faceValue: "",
    benefitAmount: "",
    termLength: ""
  });

  const [carriers, setCarriers] = useState([]);
  const [coverageTypes, setCoverageTypes] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [policyStatuses, setPolicyStatuses] = useState([]);
  const [coverageTypeLoading, setCoverageTypeLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!policy) return;

    const effectiveDate = (policy.effective_date || policy.effectiveDate || "").split("T")[0];

    setFormData({
      id: policy.id || null,
      status: policy.status || "",
      carrierId: policy.carrier_id || policy.carrierId || "",
      planName: policy.plan_name || policy.planName || policy.product_name || policy.productName || "",
      planId: policy.plan_id || policy.planId || "",
      coverageTypeId: policy.coverage_type_id || policy.coverageTypeId || "",
      coverageTypePlanTypeId:
        policy.coverage_type_plan_type_id || policy.coverageTypePlanTypeId || "",
      applicationDate: (policy.application_date || policy.applicationDate || "").split("T")[0],
      effectiveDate,
      renewalDate: (policy.renewal_date || policy.renewalDate || "").split("T")[0],
      terminationDate: (policy.termination_date || policy.terminationDate || "").split("T")[0],
      electionPeriod: policy.election_period || policy.electionPeriod || "",
      hraCompleted: policy.hra_completed || policy.hraCompleted || "",
      policyNumber: policy.policyNumber || policy.policy_number || "",
      enrollmentCode: policy.enrollmentCode || policy.enrollment_code || "",
      premium: policy.premium || "",
      benefitOption: policy.benefit_option || policy.benefitOption || "",
      faceValue: policy.face_value || policy.faceValue || "",
      benefitAmount: policy.benefit_amount || policy.benefitAmount || "",
      termLength: policy.term_length || policy.termLength || ""
    });
  }, [policy]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [coverageRes, statusesRes] = await Promise.all([
          authFetch("/api/coverage-types"),
          authFetch("/api/policy-statuses?type=policy")
        ]);

        const coverageData = await coverageRes.json().catch(() => []);
        const statusesData = await statusesRes.json().catch(() => []);

        setCoverageTypes(Array.isArray(coverageData) ? coverageData : []);
        setPolicyStatuses(Array.isArray(statusesData) ? statusesData : []);
      } catch (err) {
        console.error("Load policy modal lookup data error:", err);
        setCoverageTypes([]);
        setPolicyStatuses([]);
        setCarriers([]);
      }
    };

    loadInitialData();
  }, []);

  const selectedCoverageType = useMemo(
    () => coverageTypes.find((ct) => Number(ct.id) === Number(formData.coverageTypeId)),
    [coverageTypes, formData.coverageTypeId]
  );

  const selectedCoverageName = selectedCoverageType?.name || "";
  const shouldShowPlanId = PLAN_ID_COVERAGE_TYPES.includes(selectedCoverageName);
  const shouldShowPlanName = selectedCoverageName !== "Medicare Supplement";

  useEffect(() => {
    const loadCoverageTypeData = async () => {
      if (!formData.coverageTypeId) {
        setPlanTypes([]);
        setCarriers([]);
        return;
      }

      try {
        setCoverageTypeLoading(true);

        const [planTypesRes, carriersRes] = await Promise.all([
          authFetch(`/api/coverage-types/${formData.coverageTypeId}/plan-types`),
          authFetch(`/api/subscriber-carriers/coverage-type/${formData.coverageTypeId}`)
        ]);

        const planTypesData = await planTypesRes.json().catch(() => []);
        const carriersData = await carriersRes.json().catch(() => ({}));

        const nextPlanTypes = Array.isArray(planTypesData) ? planTypesData : [];

        const nextCarriers = Array.isArray(carriersData?.carriers)
          ? carriersData.carriers
          : Array.isArray(carriersData)
            ? carriersData
            : [];

        setPlanTypes(nextPlanTypes);
        setCarriers(nextCarriers);

        setFormData((prev) => {
          if (!prev.carrierId) return prev;

          const carrierStillAvailable = nextCarriers.some(
            (carrier) => String(getCarrierId(carrier)) === String(prev.carrierId)
          );

          if (carrierStillAvailable) return prev;

          return {
            ...prev,
            carrierId: "",
            benefitOption: "",
            benefitAmount: ""
          };
        });
      } catch (err) {
        console.error("Load policy coverage-type data error:", err);
        setPlanTypes([]);
        setCarriers([]);
      } finally {
        setCoverageTypeLoading(false);
      }
    };

    loadCoverageTypeData();
  }, [formData.coverageTypeId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "coverageTypeId") {
        return {
          ...prev,
          coverageTypeId: value,
          coverageTypePlanTypeId: "",
          carrierId: "",
          planName: "",
          planId: "",
          benefitOption: "",
          benefitAmount: "",
          termLength: "",
          terminationDate: shouldShowTerminationDate(prev.status)
            ? prev.terminationDate
            : ""
        };
      }

      if (name === "status") {
        return {
          ...prev,
          status: value,
          terminationDate: shouldShowTerminationDate(value)
            ? prev.terminationDate
            : ""
        };
      }

      if (name === "carrierId") {
        return {
          ...prev,
          carrierId: value,
          benefitOption: "",
          benefitAmount: ""
        };
      }

      return {
        ...prev,
        [name]: value
      };
    });
  };

  const selectedPlanType = useMemo(
    () => planTypes.find((pt) => String(pt.id) === String(formData.coverageTypePlanTypeId)),
    [planTypes, formData.coverageTypePlanTypeId]
  );

  const selectedCarrier = useMemo(
    () => carriers.find((c) => String(getCarrierId(c)) === String(formData.carrierId)),
    [carriers, formData.carrierId]
  );

  const summaryItems = [
    { label: "Status", value: formData.status || "Not Set" },
    {
      label: selectedCoverageName === "Medicare Supplement" ? "Plan Type" : "Plan",
      value:
        selectedCoverageName === "Medicare Supplement"
          ? selectedPlanType?.name || "Not Set"
          : formData.planName || "Not Set"
    },
    { label: "Carrier", value: selectedCarrier?.name || selectedCarrier?.carrier_name || "Not Set" },
    { label: "Premium", value: formData.premium || "Not Set" }
  ];

  const renderCarrierSelect = () => (
    <div className="form-group">
      <label>Carrier</label>
      <select
        name="carrierId"
        value={formData.carrierId}
        onChange={handleChange}
        disabled={!formData.coverageTypeId || coverageTypeLoading || carriers.length === 0}
      >
        <option value="">
          {coverageTypeLoading
            ? "-- Loading carriers --"
            : carriers.length === 0
              ? "-- No carriers configured --"
              : "-- Select Carrier --"}
        </option>
        {carriers.map((c) => {
          const carrierId = getCarrierId(c);

          return (
            <option key={carrierId} value={carrierId}>
              {c.name || c.carrier_name}
            </option>
          );
        })}
      </select>

      {formData.coverageTypeId && !coverageTypeLoading && carriers.length === 0 && (
        <small>
          No carriers have been selected for this coverage type. Manage carriers in Settings.
        </small>
      )}
    </div>
  );

  const renderPlanNameInput = () => (
    <div className="form-group">
      <label>Plan Name</label>
      <input
        type="text"
        name="planName"
        value={formData.planName}
        onChange={handleChange}
        placeholder="Enter plan/product name"
      />
    </div>
  );

  const renderPlanIdInput = () =>
    shouldShowPlanId ? (
      <div className="form-group">
        <label>Plan ID</label>
        <input
          type="text"
          name="planId"
          value={formData.planId}
          onChange={handleChange}
          placeholder="Example: H1234-001"
        />
      </div>
    ) : null;

  const renderCoverageSpecificFields = () => {
    switch (selectedCoverageName) {
      case "Dental":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
          </>
        );

      case "MAPD":
      case "PDP":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
            {renderPlanIdInput()}

            <div className="form-group">
              <label>Enrollment Period</label>
              <select
                name="electionPeriod"
                value={formData.electionPeriod}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="IEP">IEP</option>
                <option value="ICEP">ICEP</option>
                <option value="OEP">OEP</option>
                <option value="AEP">AEP</option>
                <option value="SEP">SEP</option>
              </select>
            </div>

            {selectedCoverageName === "MAPD" && (
              <div className="form-group">
                <label>HRA Completed</label>
                <select name="hraCompleted" value={formData.hraCompleted} onChange={handleChange}>
                  <option value="">-- Select --</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            )}
          </>
        );

      case "Medicare Supplement":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <select
                name="coverageTypePlanTypeId"
                value={formData.coverageTypePlanTypeId}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                {planTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.name}
                  </option>
                ))}
              </select>
            </div>

            {renderCarrierSelect()}

            <div className="form-group">
              <label>Enrollment Period</label>
              <select
                name="electionPeriod"
                value={formData.electionPeriod}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Open Enrollment">Open Enrollment</option>
                <option value="Underwritten">Underwritten</option>
                <option value="Guaranteed Issue">Guaranteed Issue</option>
              </select>
            </div>
          </>
        );

      case "Life":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <select
                name="coverageTypePlanTypeId"
                value={formData.coverageTypePlanTypeId}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                {planTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.name}
                  </option>
                ))}
              </select>
            </div>

            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            <div className="form-group">
              <label>Face Value</label>
              <input
                type="number"
                name="faceValue"
                value={formData.faceValue}
                onChange={handleChange}
              />
            </div>
          </>
        );

      case "Individual Health":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <select
                name="coverageTypePlanTypeId"
                value={formData.coverageTypePlanTypeId}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                {planTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.name}
                  </option>
                ))}
              </select>
            </div>

            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            {selectedPlanType?.name === "Short-Term Health" && (
              <>
                <div className="form-group">
                  <label>Benefit Amount</label>
                  <input
                    type="number"
                    name="benefitAmount"
                    value={formData.benefitAmount}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Term Length (months)</label>
                  <input
                    type="number"
                    name="termLength"
                    value={formData.termLength}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
          </>
        );

      default:
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
          </>
        );
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      const payload = {
        id: formData.id,
        status: formData.status || null,
        carrier_id: parseInt(formData.carrierId, 10) || null,
        product_id: null,
        plan_name: shouldShowPlanName ? formData.planName || null : null,
        plan_id: shouldShowPlanId ? formData.planId || null : null,
        coverage_type_id: parseInt(formData.coverageTypeId, 10) || null,
        coverage_type_plan_type_id: formData.coverageTypePlanTypeId
          ? parseInt(formData.coverageTypePlanTypeId, 10)
          : null,
        application_date: formData.applicationDate || null,
        effective_date: formData.effectiveDate || null,
        renewal_date: formData.renewalDate || null,
        termination_date: shouldShowTerminationDate(formData.status)
          ? formData.terminationDate || null
          : null,
        election_period: formData.electionPeriod || null,
        hra_completed: formData.hraCompleted || null,
        policy_number: formData.policyNumber || null,
        enrollment_code: formData.enrollmentCode || null,
        premium: formData.premium || null,
        benefit_option: formData.benefitOption || null,
        face_value: formData.faceValue || null,
        benefit_amount: formData.benefitAmount || null,
        term_length: formData.termLength || null,
        plan_year: getPlanYearFromDate(formData.effectiveDate) || null,
        client_id: policy.client_id || policy.clientId || null,
        lead_id: policy.lead_id || policy.leadId || null,
        record_type: "policy"
      };

      await onSave(payload);
      setSaveMessage("Policy updated successfully.");
      setTimeout(() => setSaveMessage(""), 2200);
    } catch (err) {
      console.error("Save policy error:", err);
      setSaveMessage(err.message || "Failed to save policy.");
      setTimeout(() => setSaveMessage(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await onDelete(formData.id, formData.coverageTypeId);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error("Delete policy error:", err);
      setSaveMessage(err.message || "Failed to delete policy.");
      setTimeout(() => setSaveMessage(""), 3000);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal modal-lg policy-modal redesigned-policy-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-shell">
            <div className="modal-header policy-modal-header">
              <div>
                <h3>{selectedCoverageName ? `${selectedCoverageName} Policy` : "Edit Policy"}</h3>
                <p className="modal-subtitle">
                  Update carrier, plan name, policy details, dates, and enrollment information.
                </p>
              </div>

              <button type="button" className="modal-close-btn" onClick={onClose}>
                ×
              </button>
            </div>

            <div className="modal-body policy-modal-body">
              <div className="policy-summary-row">
                {summaryItems.map((item) => (
                  <div key={item.label} className="policy-summary-card">
                    <div className="policy-summary-label">{item.label}</div>
                    <div className="policy-summary-value">{item.value}</div>
                  </div>
                ))}
              </div>

              <div className="policy-edit-panel">
                <div className="policy-panel-head">
                  <h4>Edit Policy</h4>
                  <p>Everything important in one compact workspace.</p>
                </div>

                <div className="policy-form-grid">
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status} onChange={handleChange}>
                      <option value="">-- Select --</option>
                      {policyStatuses.map((status) => (
                        <option key={status.id} value={status.name}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Application Date</label>
                    <input
                      type="date"
                      name="applicationDate"
                      value={formData.applicationDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Effective Date</label>
                    <input
                      type="date"
                      name="effectiveDate"
                      value={formData.effectiveDate}
                      onChange={handleChange}
                    />
                  </div>

                  {renderCoverageSpecificFields()}

                  <div className="form-group">
                    <label>Renewal Date</label>
                    <input
                      type="date"
                      name="renewalDate"
                      value={formData.renewalDate}
                      onChange={handleChange}
                    />
                  </div>

                  {shouldShowTerminationDate(formData.status) && (
                    <div className="form-group">
                      <label>Termination Date</label>
                      <input
                        type="date"
                        name="terminationDate"
                        value={formData.terminationDate}
                        onChange={handleChange}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Policy Number</label>
                    <input
                      name="policyNumber"
                      value={formData.policyNumber}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Enrollment Code</label>
                    <input
                      name="enrollmentCode"
                      value={formData.enrollmentCode}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Premium</label>
                    <input
                      type="number"
                      name="premium"
                      value={formData.premium}
                      onChange={handleChange}
                    />
                  </div>

                  <CustomFieldsSection
                    recordType="policy"
                    recordId={policy.id}
                  />
                </div>
              </div>

              {saveMessage && (
                <div className="modal-status-message success">
                  {saveMessage}
                </div>
              )}
            </div>

            <div className="modal-footer policy-modal-footer">
              <div className="modal-footer-left">
                <button
                  type="button"
                  className="modal-danger-link"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Policy
                </button>
              </div>

              <div className="modal-footer-right">
                <button type="button" className="modal-button" onClick={onClose}>
                  Close
                </button>
                <button
                  type="button"
                  className="modal-button save"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-shell">
              <div className="modal-header">
                <div>
                  <h3>Delete Policy</h3>
                  <p className="modal-subtitle">
                    Remove this policy from the record permanently.
                  </p>
                </div>

                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="modal-warning-card">
                  <strong>Are you sure?</strong>
                  <br />
                  <br />
                  This action cannot be undone and will permanently remove the policy.
                </div>
              </div>

              <div className="modal-footer">
                <div className="modal-footer-left" />
                <div className="modal-footer-right">
                  <button
                    type="button"
                    className="modal-button"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="modal-button danger"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting..." : "Delete Policy"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PolicyModal;